import React, {useEffect} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {BackTo} from 'components/common/BackTo';
import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {DatePicker} from 'components/ui-kit/DatePicker';
import {Input} from 'components/ui-kit/Input';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {
    recallDataChanged,
    showRecallApproveModalChanged,
    uploadState$,
} from 'models/registries/gtinsGrid/upload';

import {RecallData} from 'types/gtins';
import {useDropoutDictionaryOptions} from 'utils/dropoutDictionary';

import {RecallApproveModal} from './RecallApproveModal';
import {
    ButtonInner,
    ContentWrapper,
    DatePickerField,
    FormField,
    FormWrapper,
    HeadlineText,
    NavigationButtonsContainer,
    SubheadlineText,
} from './styled';

const BATCH_NUMBER_REGEX = /^[A-Z0-9_-]{1,50}$/;

const schema = yup.object().shape({
    batchNumberSource: yup
        .mixed<'byCode' | 'byCodeAndBatchNumber'>()
        .oneOf(['byCode', 'byCodeAndBatchNumber'])
        .required(),
    batchNumber: yup.string().when('batchNumberSource', {
        is: 'byCodeAndBatchNumber',
        then: (s) =>
            s
                .required(i18next.t('errors.batchNumber.required'))
                .matches(BATCH_NUMBER_REGEX, i18next.t('errors.batchNumber.invalidFormat')),
        otherwise: (s) =>
            s
                .notRequired()
                .nullable()
                .matches(BATCH_NUMBER_REGEX, {
                    message: i18next.t('errors.batchNumber.invalidFormat'),
                    excludeEmptyString: true,
                }),
    }),
    reason: yup.string().required(i18next.t('components.form.required')),
    productCode: yup.string().required(i18next.t('components.form.required')),
    recallDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
    documentNumber: yup.string().when('batchNumberSource', {
        is: 'byCodeAndBatchNumber',
        then: (s) => s.notRequired().nullable(),

        otherwise: (s) => s.required(i18next.t('errors.batchNumber.required')),
    }),
    comment: yup.string().nullable(),
});

export const RecallPage = () => {
    const {t} = useTranslation();
    const {reasons} = useDropoutDictionaryOptions();
    const {isRecallPending} = useStore(uploadState$);
    const {control, register, handleSubmit, formState, setValue, watch} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            batchNumberSource: 'byCode',
            reason: '',
            batchNumber: '',
            productCode: '',
            recallDate: null,
            documentNumber: '',
            comment: '',
        },
    });

    useEffect(() => {
        register('batchNumberSource');
    }, [register]);

    const batchNumberSource = watch('batchNumberSource');

    const onSubmit = (data: RecallData) => {
        recallDataChanged(data);
        showRecallApproveModalChanged(true);
    };

    return (
        <>
            <BackTo to={ROUTE.REGISTRIES_GTIN}>{t('pages.documents.back')}</BackTo>
            <RecallApproveModal />
            <ContentWrapper>
                <HeadlineText>{t('pages.products.recallForm.title')}</HeadlineText>
                <SubheadlineText>{t('pages.products.recallForm.description')}</SubheadlineText>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormWrapper>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Button
                                    style={{height: 'fit-content'}}
                                    variant={
                                        batchNumberSource === 'byCodeAndBatchNumber'
                                            ? ButtonVariant.Outline
                                            : ButtonVariant.OutlineSecondary
                                    }
                                    type="button"
                                    onClick={() => {
                                        setValue('batchNumberSource', 'byCode', {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <ButtonInner
                                        $active={batchNumberSource === 'byCodeAndBatchNumber'}
                                    >
                                        <span>{t('pages.products.recallForm.byProductCode')}</span>
                                        <span>
                                            {t('pages.products.recallForm.recallProductByGTIN')}
                                        </span>
                                    </ButtonInner>
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Button
                                    style={{height: 'fit-content'}}
                                    variant={
                                        batchNumberSource === 'byCode'
                                            ? ButtonVariant.Outline
                                            : ButtonVariant.OutlineSecondary
                                    }
                                    type="button"
                                    onClick={() => {
                                        setValue('batchNumberSource', 'byCodeAndBatchNumber', {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <ButtonInner $active={batchNumberSource === 'byCode'}>
                                        <span>
                                            {t(
                                                'pages.products.recallForm.byProductCodeAndBatchNumber',
                                            )}
                                        </span>
                                        <span>{t('pages.products.recallForm.recallBatch')}</span>
                                    </ButtonInner>
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="reason"
                                        render={(props) => (
                                            <Select
                                                isRequired
                                                disabled={isRecallPending}
                                                onChange={(val: SelectOption<string>) => {
                                                    props.onChange(val.value);
                                                }}
                                                value={
                                                    reasons.filter(
                                                        (el: SelectOption<string>) =>
                                                            el.value === props.value,
                                                    )[0]
                                                }
                                                label={t(
                                                    'pages.products.recallForm.inputTitles.reason',
                                                )}
                                                hasError={!!formState.errors.reason}
                                                options={reasons}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="productCode"
                                    render={({onChange, value}) => (
                                        <Input
                                            disabled={isRecallPending}
                                            isRequired
                                            label={t(
                                                'pages.products.recallForm.inputTitles.productCode',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.productCode',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.productCode}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid
                            style={{
                                display:
                                    batchNumberSource === 'byCodeAndBatchNumber' ? 'block' : 'none',
                            }}
                            item
                            xs={12}
                            md={12}
                        >
                            <FormField>
                                <Controller
                                    control={control}
                                    name="batchNumber"
                                    render={({onChange, value}) => (
                                        <Input
                                            disabled={isRecallPending}
                                            isRequired
                                            label={t(
                                                'pages.products.recallForm.inputTitles.batchNumber',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.batchNumber',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.batchNumber}
                                        />
                                    )}
                                />
                                {formState.errors.batchNumber && (
                                    <span style={{fontSize: 12, color: 'red'}}>
                                        {t(formState.errors.batchNumber.message as string)}
                                    </span>
                                )}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="documentNumber"
                                    render={({onChange, value}) => (
                                        <Input
                                            isRequired={batchNumberSource === 'byCode'}
                                            label={t(
                                                'pages.products.recallForm.inputTitles.regulatorDocumentNumber',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.regulatorDocumentNumber',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.documentNumber}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <DatePickerField>
                                        <Controller
                                            control={control}
                                            name="recallDate"
                                            render={({onChange, value}) => (
                                                <DatePicker
                                                    placeholderText={t(
                                                        'pages.products.recallForm.inputPlaceholders.recallDecisionDate',
                                                    )}
                                                    isRequired
                                                    label={t(
                                                        'pages.products.recallForm.inputTitles.recallDecisionDate',
                                                    )}
                                                    maxDate={new Date()}
                                                    value={value}
                                                    onChange={(newDate: Date | null) => {
                                                        onChange(newDate);
                                                    }}
                                                    hasError={!!formState.errors.recallDate}
                                                />
                                            )}
                                        />
                                    </DatePickerField>
                                </FormField>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="comment"
                                        render={({onChange, value}) => (
                                            <Input
                                                label={t(
                                                    'pages.products.recallForm.inputTitles.comment',
                                                )}
                                                placeholder={t(
                                                    'pages.products.recallForm.inputPlaceholders.comment',
                                                )}
                                                value={value}
                                                onChange={onChange}
                                                hasError={!!formState.errors.comment}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                        </Grid>
                        <NavigationButtonsContainer>
                            <div>
                                <Button
                                    isLoading={isRecallPending}
                                    style={{width: 'auto'}}
                                    variant={ButtonVariant.Outline}
                                    type="button"
                                    onClick={() => {
                                        history.push(ROUTE.REGISTRIES_GTIN);
                                    }}
                                >
                                    {t('pages.products.recallForm.cancel')}
                                </Button>
                            </div>
                            <div>
                                <Button
                                    isLoading={isRecallPending}
                                    style={{width: 'auto'}}
                                    variant={ButtonVariant.Normal}
                                    type="submit"
                                    disabled={!formState.isValid}
                                >
                                    {t('pages.products.recallForm.recall')}
                                </Button>
                            </div>
                        </NavigationButtonsContainer>
                    </FormWrapper>
                </form>
            </ContentWrapper>
        </>
    );
};
